import { Loader, Pause, Play, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';

import { StopButton } from './stop-button';
import { TimeTrackingProjectSelector } from './time-tracking-project-selector';

/**
 * FlatView - Horizontal inline timer layout.
 *
 * Displays project, timer and control buttons in a single row.
 * Suitable for toolbars or headers where a compact horizontal layout is needed.
 */
interface FlatViewProps {
  displayTime: string;
  className?: string;
  selectedProject: DropdownSearchItem | null;
  isActive: boolean;
  isPaused: boolean;
  isLimitReached: boolean;

  // Loading state per action — drives spinners and disables controls so the
  // user doesn't perceive lag and can't fire duplicate requests.
  isStartPending?: boolean;
  isTogglePausePending?: boolean;
  isStopPending?: boolean;
  isDiscardPending?: boolean;
  /** Any timer action currently in flight — disables all controls. */
  isAnyActionPending?: boolean;

  // Handlers
  openCreateModal: () => void;
  openProjectSelector: () => void;
  toggle: () => void;
  togglePause: () => void;
  reset: () => void;
}

export function FlatView({
  displayTime,
  className,
  selectedProject,
  isActive,
  isPaused,
  isLimitReached,
  isStartPending,
  isTogglePausePending,
  isStopPending,
  isDiscardPending,
  isAnyActionPending,
  openCreateModal,
  openProjectSelector,
  toggle,
  togglePause,
  reset,
}: FlatViewProps) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <TimeTrackingProjectSelector
        selectedProject={selectedProject}
        onClick={openProjectSelector}
      />
      {/* Timer display */}
      <span
        className={cn('text-sm font-medium', !isActive && 'text-muted-foreground')}
        onClick={toggle}
      >
        {displayTime}
      </span>

      {/* Control buttons */}
      <div className="flex items-center gap-3">
        {isActive && !isLimitReached && (
          <Button
            variant="default"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={togglePause}
            disabled={isAnyActionPending}
          >
            {isTogglePausePending ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : isPaused ? (
              <Play fill="currentColor" className="h-5 w-5" />
            ) : (
              <Pause fill="currentColor" className="h-5 w-5" />
            )}
          </Button>
        )}

        {isActive && (
          <StopButton toggle={toggle} isLoading={isStopPending} disabled={isAnyActionPending} />
        )}

        {!isActive && (
          <Button
            variant="default"
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={toggle}
            disabled={isStartPending}
          >
            {isStartPending ? (
              <Loader className="h-5 w-5 animate-spin" />
            ) : (
              <Play fill="currentColor" className="h-5 w-5" />
            )}
          </Button>
        )}

        {!isActive && (
          <Button
            variant="secondary"
            size="icon"
            className="h-8 w-8 rounded-full bg-muted-foreground/15 hover:bg-muted-foreground/25"
            onClick={openCreateModal}
            disabled={isStartPending}
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}

        {isActive && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full text-muted-foreground hover:text-destructive"
            onClick={reset}
            disabled={isAnyActionPending}
          >
            {isDiscardPending ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
